<template>
  <vx-card class="editor-mid-card shadow-none border border-solid border-grey-light">
    <div class="mb-6 border-b pb-4">
      <div class="flex justify-between items-center flex-wrap">
        <div class="flex items-center">
          <h3 class="text-xl font-bold mr-4">{{ $t('editor.offers.title') || 'My Offers' }}</h3>
          <vx-tooltip v-if="saveInProgress" text="Saving to cloud...">
            <feather-icon icon="CloudIcon" class="w-5 h-5 text-success" />
          </vx-tooltip>
          <vx-tooltip v-else text="Saved to cloud">
            <feather-icon icon="CloudIcon" class="w-5 h-5 text-grey" />
          </vx-tooltip>
        </div>
        <div>
          <options :previousMove="{ name: 'editor-projects' }" :nextMove="{ name: 'editor-offers' }" />
        </div>
      </div>
    </div>

    <!-- LOADING ACCESS CHECK -->
    <div v-if="isLoadingOrgs" class="p-4 bg-primary text-white rounded-lg my-4 shadow-sm flex items-center">
      <feather-icon icon="ClockIcon" svgClasses="w-5 h-5 mr-3" /> 
      <span>Verifying access permissions...</span>
    </div>

    <!-- CREDIBILITY & ACCESS CHECK -->
    <div v-else-if="!hasConsultantAccess" class="p-4 bg-warning text-white rounded-lg my-4 shadow-sm">
      <h4 class="text-white mb-2 flex items-center"><feather-icon icon="LockIcon" svgClasses="w-5 h-5 mr-2" /> Restricted Access</h4>
      <p class="text-sm">The Offers tab is exclusively available for verified Consultants and Coaches. To maintain platform credibility, only approved profiles can list services here.</p>
    </div>

    <div v-else class="mb-2">
      <!-- OFFERS LIST -->
      <div v-if="!addForm">
        <!-- CREDIBILITY BANNER -->
        <div class="mb-6 p-3 bg-blue-50 border border-blue-100 rounded-lg text-sm text-primary flex items-center">
          <feather-icon icon="ShieldIcon" svgClasses="w-5 h-5 mr-3 text-primary" />
          <span><strong>Credibility Note:</strong> All offers are visible on your public profile. Ensure descriptions are accurate and professional.</span>
        </div>

        <div v-if="offers.length" class="bg-grey-lightest rounded-lg p-4 border border-solid border-grey-light">
          <VuePerfectScrollbar class="scroll-area--list" :settings="settings">
            <draggable v-model="offers" :disabled="draggableDisabled" class="list-groupp" ghost-class="ghost-card"
              :move="checkMove" @start="dragging = true" @change="updateOrder">
              
              <div :key="indextr" v-for="(tr, indextr) in offers" class="flex items-center bg-white p-3 mb-3 rounded-lg shadow-sm border border-solid border-grey-light cursor-move hover:shadow-md transition-shadow">
                <div class="w-10 flex justify-center">
                  <feather-icon icon="MoveIcon" svgClasses="h-5 w-5 text-grey" />
                </div>
                
                <div class="w-16 mr-4 flex-shrink-0">
                <div 
                    class="w-full h-16 rounded shadow-inner border border-grey-light border-opacity-50" 
                    :style="{ backgroundColor: tr.profileColor || '#f29200' }">
                </div>
             </div>
                
                <div class="flex-1 overflow-hidden pr-4 text-left flex flex-col justify-center">
  <h4 class="font-semibold text-dark truncate">{{ tr.title || 'Untitled Offer' }}</h4>
  <p class="text-sm text-grey-dark truncate mt-1">{{ tr.valuePromise || tr.shortDescription || 'No description provided.' }}</p>
</div>
                
                <div class="w-32 text-success font-semibold ml-auto whitespace-nowrap text-right">
                  {{ tr.price ? `€ ${tr.price}` : 'Free / Custom' }}
                </div>
                
                <div class="w-20 flex justify-end gap-2 pl-4 pr-2 border-l ml-4 border-grey-light">
                  <vx-tooltip text="Edit Offer">
                    <feather-icon icon="Edit2Icon" svgClasses="w-5 h-5 text-grey-dark hover:text-primary cursor-pointer transition-colors" @click.stop="editOffer(tr)" />
                  </vx-tooltip>
                  <vx-tooltip text="Delete Offer">
                    <feather-icon icon="Trash2Icon" svgClasses="w-5 h-5 text-grey-dark hover:text-danger cursor-pointer transition-colors" @click.stop="deleteOffer(tr._id)" />
                  </vx-tooltip>
                </div>
              </div>
            </draggable>
          </VuePerfectScrollbar>
        </div>
        <div v-else class="text-center py-10 bg-grey-lightest rounded-lg border-dashed border-2 border-grey-light">
          <feather-icon icon="BriefcaseIcon" svgClasses="w-12 h-12 text-grey mb-3 mx-auto block" />
          <p class="text-grey-dark text-lg font-medium">No offers created yet.</p>
          <p class="text-sm text-grey mt-1">Start adding your consulting services to attract clients!</p>
        </div>

        <div v-if="isUserLoggedIn" class="mt-6 w-full flex justify-start pl-2">
  <span 
    @click="addOffer()" 
    class="add-text-btn"
  >
    + Add Offer
  </span>
</div>
      </div>

      <!-- ADD/EDIT FORM -->
      <div v-if="addForm" class="mt-2 relative">
        
        <!-- Left Aligned Header -->
        <!-- <div class="mb-6 border-b pb-3 pt-2">
          <h4 class="font-bold text-xl text-dark">{{ localData._id ? 'Edit Offer' : 'Create New Offer' }}</h4>
          <p class="text-sm text-grey-dark mt-1">Define the details, scope, and expectations for this service.</p>
        </div> -->

        <div class="max-w-4xl mx-auto pb-6">
          
          <!-- Full Width Offer Title -->
          <div class="vx-row mb-6">
            <div class="vx-col w-full">
              <div class="flex justify-between items-end mb-1">
                <label class="font-semibold text-sm text-dark">Offer Title *</label>
                <span class="text-xs text-grey">{{ (localData.title || '').length }} / 90</span>
              </div>
              <vs-input class="w-full modern-input" name="Title" maxlength="90" :disabled="disableInputField"
                v-model="localData.title" @blur="saveField('saveOfferTitle')" placeholder="e.g. AI Strategy & Automation Consulting" />
            </div>
          </div>

          <!-- Single-line Value Promise -->
          <div class="vx-row mb-6">
            <div class="vx-col w-full">
              <div class="flex justify-between items-end mb-1">
                <label class="font-semibold text-sm text-dark">Value Promise *</label>
                <span class="text-xs text-grey">{{ (localData.valuePromise || '').length }} / 200</span>
              </div>
              <vs-input class="w-full modern-input" name="ValuePromise" maxlength="200" :disabled="disableInputField"
                v-model="localData.valuePromise" @blur="saveField('saveOfferValuePromise')" 
                placeholder="Explain in one sentence what useful result the client can expect from this offer. Focus on the decision, capability, reduced risk or opportunity created." />
            </div>
          </div>

          <!-- Client Outcome & Deliverables -->
          <div class="vx-row mb-8 border-b border-gray-200 pb-4 items-stretch">
            
            <!-- Left Column: Outcome (Flex container to allow stretching) -->
            <div class="vx-col w-full md:w-1/2 mb-6 md:mb-0 pr-0 md:pr-4 flex flex-col">
              <div class="flex justify-between items-end mb-1">
                <label class="font-semibold text-sm text-dark">Client Outcome *</label>
                <span class="text-xs text-grey">{{ (localData.clientOutcome || '').length }} / 500</span>
              </div>
              <!-- Added flex-1 and stretchy-textarea classes here -->
              <vs-textarea class="w-full modern-textarea flex-1 stretchy-textarea mb-0" maxlength="500" :disabled="disableInputField"
                v-model="localData.clientOutcome" @blur="saveField('saveOfferOutcome')" 
                placeholder="What will the client be able to decide, do or improve after this engagement? Describe a result that the consultant and client can reasonably influence." />
            </div>

            <!-- Right Column: Deliverables & Buttons -->
            <div class="vx-col w-full md:w-1/2 pl-0 md:pl-4 flex flex-col">
              <label class="font-semibold text-sm text-dark block mb-2">What The Client Receives *</label>
              
              <!-- Dynamic List Container -->
              <div class="flex flex-col gap-3 mb-1" v-if="localData.deliverables && localData.deliverables.length > 0">
                <div v-for="(item, index) in localData.deliverables" :key="index" class="flex items-center group">
                  <vs-input 
                    :value="localData.deliverables[index]" 
                    @input="val => updateDeliverable(index, val)"
                    class="w-full modern-input force-left-align" 
                    :placeholder="getDeliverablePlaceholder(index)" 
                    @blur="saveField('saveOfferDeliverables')" 
                  />
                  <feather-icon icon="Trash2Icon" class="w-5 h-5 ml-3 cursor-pointer text-grey hover:text-danger transition-colors opacity-50 group-hover:opacity-100" 
                    @click="removeDeliverable(index)" />
                </div>
              </div>

              <!-- Add Item Trigger -->
              <div class="flex mt-2">
                <div class="text-sm font-semibold text-dark cursor-pointer flex items-center hover:text-primary transition-colors w-max" @click="addDeliverable">
                  <feather-icon icon="PlusIcon" class="w-4 h-4 mr-1"/> Add item
                </div>
              </div>
              <!-- <p class="text-xs text-grey-dark mt-2 mb-4">List tangible outputs that allow the client to verify that the work was completed.</p> -->

              <!-- Subtle Popup Triggers (Stacked matching 'Add item') -->
              <div class="flex flex-col gap-3 mt-auto pt-2">
                <div @click="buyerConfidencePopup = true" 
                     class="text-sm font-semibold cursor-pointer flex items-center transition-colors select-none w-max"
                     :class="hasBuyerConfidence ? 'text-success' : 'text-dark hover:text-primary'">
                  <feather-icon :icon="hasBuyerConfidence ? 'CheckIcon' : 'PlusIcon'" class="w-4 h-4 mr-1" />
                  Add Buyer Confidence
                </div>
                
                <div @click="scopePopup = true" 
                     class="text-sm font-semibold cursor-pointer flex items-center transition-colors select-none w-max"
                     :class="hasScope ? 'text-success' : 'text-dark hover:text-primary'">
                  <feather-icon :icon="hasScope ? 'CheckIcon' : 'PlusIcon'" class="w-4 h-4 mr-1" />
                  Scope and Expectations
                </div>
              </div>
              <!-- <p class="text-xs text-grey-dark mt-2 mb-4">List tangible outputs that allow the client to verify that the work was completed.</p> -->
            </div>
          </div>

          <!-- 3 Dropdowns Row: Available Orgs, Best Fit, Engagement Format -->
          <div class="vx-row mb-4">
            <div class="vx-col w-full md:w-1/3 mb-4 md:mb-0">
              <label class="font-semibold text-sm text-dark block mb-1">Available To Organization(s) *</label>
              <vs-select multiple class="w-full modern-input" v-model="selectedOrgObject" @change="onOrgChange" :disabled="disableInputField" placeholder="Select Orgs...">
                <vs-select-item :key="index" :value="item" :text="item.name" v-for="(item, index) in myConsultantOrgs" />
              </vs-select>
              <p class="text-xs text-grey-dark mt-1">Tap an organization icon to make it available there.</p>
            </div>

            <div class="vx-col w-full md:w-1/3 mb-4 md:mb-0">
              <label class="font-semibold text-sm text-dark block mb-1">Best Fit For *</label>
              <vs-select class="w-full modern-input" v-model="localData.bestFitFor" @change="saveField('saveOfferBestFit')" :disabled="disableInputField">
                 <vs-select-item value="" text="Select the type of client" />
                 <vs-select-item v-for="opt in bestFitOptions" :key="opt" :value="opt" :text="opt" />
              </vs-select>
            </div>

            <div class="vx-col w-full md:w-1/3">
              <label class="font-semibold text-sm text-dark block mb-1">Engagement Format *</label>
              <vs-select class="w-full modern-input" v-model="localData.format" @change="saveField('saveOfferFormat')" :disabled="disableInputField">
                <vs-select-item value="" text="Select how the engagement will be..." />
                <vs-select-item :key="index" :value="item.value" :text="item.text" v-for="(item, index) in evidenceFormats" />
              </vs-select>
            </div>
          </div>

          <!-- Delivery, Price & Payment Basis -->
          <div class="vx-row mb-4">
            <div class="vx-col w-full md:w-1/3 mb-4 md:mb-0">
              <label class="font-semibold text-sm text-dark block mb-1">Delivery Window *</label>
              <vs-select class="w-full modern-input" v-model="localData.deliveryWindow" @change="saveField('saveOfferDeliveryWindow')" :disabled="disableInputField">
                 <vs-select-item value="" text="Select a typical delivery window" />
                 <vs-select-item v-for="opt in deliveryWindowOptions" :key="opt" :value="opt" :text="opt" />
              </vs-select>
              <p class="text-xs text-grey-dark mt-1">This sets delivery expectations. It is not the billing unit.</p>
            </div>

            <div class="vx-col w-full md:w-1/3 mb-4 md:mb-0">
              <label class="font-semibold text-sm text-dark block mb-1">Offer Price (€) *</label>
              <vs-input class="w-full modern-input" name="Price" maxlength="50" :disabled="disableInputField"
                  v-model="localData.price" @blur="saveField('saveOfferPrice')" placeholder="150" />
              <p class="text-xs text-grey-dark mt-1">Price the complete offer and evidence package.</p>
            </div>

            <div class="vx-col w-full md:w-1/3">
              <label class="font-semibold text-sm text-dark block mb-1">Payment Basis *</label>
              <vs-select class="w-full modern-input" v-model="localData.paymentBasis" @change="saveField('saveOfferPaymentBasis')" :disabled="disableInputField">
                 <vs-select-item value="" text="Select payment terms" />
                 <vs-select-item v-for="opt in paymentBasisOptions" :key="opt" :value="opt" :text="opt" />
              </vs-select>
            </div>
          </div>   

          <!-- Offer Banner Style (Side-by-Side) -->
          <div class="vx-row mb-0 mt-6 border-t border-gray-200 pt-6">
            
            <!-- Left Side: Color Selection -->
            <div class="vx-col w-full md:w-1/2 flex flex-col justify-center mb-6 md:mb-0 pr-0 md:pr-4">
              <label class="font-semibold text-sm text-dark block mb-2">Card Color *</label>
              <p class="text-xs text-grey-dark mb-4">Select a background color for your offer card to help it stand out on your profile.</p>
              
              <!-- Interactive Profile Color Swatches -->
              <div class="flex items-center gap-3">
                  <div v-for="color in profileColors" :key="color.value" 
                      @click="setProfileColor(color.value)"
                      class="w-8 h-8 rounded-full cursor-pointer transition-all hover:scale-110 shadow-sm border-2"
                      :style="{ backgroundColor: color.value }"
                      :class="localData.profileColor === color.value ? 'border-dark scale-110 ring-2 ring-offset-1 ring-grey-light' : 'border-transparent'">
                  </div>
              </div>
            </div>

            <!-- Right Side: Real-time Preview -->
            <div class="vx-col w-full md:w-1/2 pl-0 md:pl-4">
              <label class="font-semibold text-sm text-dark block mb-2">Card Preview</label>
              
              <!-- Dynamic Colored Banner Preview -->
              <div class="relative w-full h-32 rounded-lg overflow-hidden text-dark transition-colors flex flex-col justify-center px-6 z-0 shadow-md border border-grey-light" 
                      :style="{ backgroundColor: localData.profileColor || '#f29200' }">
                  <h2 class="text-lg font-bold leading-tight truncate">{{ localData.title || 'Your offer title' }}</h2>
                  <p class="text-sm mt-1 text-black opacity-80 truncate">{{ localData.bestFitFor || 'Select best fit profile' }}</p>
              </div>
            </div>
            
          </div>

          <!-- <p class="text-center text-xs text-grey mt-10">Nothing is visible to buyers until you publish.</p> -->

          <!-- Bottom Action Buttons -->
          <!-- <div class="flex justify-end gap-3 mt-8 pt-6 border-t border-grey-light">
             <vs-button type="border" color="dark" class="font-medium rounded-lg" @click="addForm = false; localData = {};">Cancel</vs-button>
             <vs-button color="success" class="font-medium rounded-lg shadow-sm text-white bg-success" @click="addForm = false">Save & Publish</vs-button>
          </div> -->
        </div>

        <!-- BUYER CONFIDENCE POPUP -->
        <vs-popup title="Buyer Confidence" :active.sync="buyerConfidencePopup" class="offer-modal">
          <div class="mb-4">
            <label class="font-semibold text-sm text-dark block mb-3">Low-Risk First Step</label>
            <div class="grid grid-cols-1 gap-3 mb-4">
              <div v-for="opt in firstStepOptions" :key="opt.value" @click="toggleFirstStep(opt.value)"
                    class="border p-3 rounded-lg cursor-pointer transition-all border-solid"
                    :class="localData.firstStepType === opt.value ? 'border-warning bg-warning-light border-2' : 'border-grey-light hover:border-grey bg-white'">
                  <h5 class="font-semibold text-sm mb-1 flex items-center" :class="localData.firstStepType === opt.value ? 'text-dark' : 'text-grey-dark'">
                    <feather-icon v-if="localData.firstStepType === opt.value" icon="CheckIcon" class="w-4 h-4 text-warning mr-1 stroke-current stroke-2"/>
                    {{ opt.title }}
                  </h5>
                  <p class="text-xs" :class="localData.firstStepType === opt.value ? 'text-dark' : 'text-grey'">{{ opt.desc }}</p>
              </div>
            </div>
            
            <label class="font-semibold text-sm text-dark block mb-2 mt-5">What The Buyer Walks Away With From That First Step</label>
            <vs-input class="w-full modern-input mb-1" v-model="localData.firstStepTakeaway" @blur="saveField('saveOfferTakeaway')"
              placeholder="e.g. A written note naming the three risks I can see in your current setup" />
            <p class="text-xs text-grey-dark mt-1 block mb-5">Without this, buyers read the free step as a sales call and skip it.</p>
          </div>

          <div class="vx-row mb-6">
            <div class="vx-col w-full sm:w-1/2 mb-4 sm:mb-0">
              <label class="font-semibold text-sm text-dark block mb-1">Price Flexibility</label>
              <vs-select class="w-full modern-input" v-model="localData.priceFlexibility" @change="saveField('saveOfferPriceFlex')">
                <vs-select-item v-for="opt in priceFlexibilityOptions" :key="opt" :value="opt" :text="opt" />
              </vs-select>
            </div>
            <div class="vx-col w-full sm:w-1/2 flex flex-col justify-center pt-2">
              <div class="flex items-center">
                  <vs-switch color="warning" v-model="localData.valueExchangeAccepted" @change="saveField('saveOfferValEx')" />
                  <span class="ml-3 font-semibold text-sm" :class="localData.valueExchangeAccepted ? 'text-dark' : 'text-grey'">Value exchange accepted</span>
              </div>
            </div>
          </div>

          <div class="vx-row mb-2">
            <div class="vx-col w-full sm:w-1/2 mb-4 sm:mb-0">
              <label class="font-semibold text-sm text-dark block mb-1 flex items-center">
                <feather-icon icon="ClockIcon" class="w-4 h-4 mr-1 text-grey" /> Response Time
              </label>
              <vs-select class="w-full modern-input" v-model="localData.responseTime" @change="saveField('saveOfferResponse')">
                <vs-select-item v-for="opt in responseTimeOptions" :key="opt" :value="opt" :text="opt" />
              </vs-select>
            </div>
            <div class="vx-col w-full sm:w-1/2">
              <label class="font-semibold text-sm text-dark block mb-1">Slots Open This Month</label>
              <vs-input class="w-full modern-input mb-1" type="number" v-model="localData.slotsOpen" @blur="saveField('saveOfferSlots')" placeholder="e.g. 2" />
            </div>
          </div>
          <div class="flex justify-end mt-6 pt-4 border-t border-grey-light">
            <vs-button color="primary" @click="buyerConfidencePopup = false">Done</vs-button>
          </div>
        </vs-popup>

        <!-- SCOPE AND EXPECTATIONS POPUP -->
        <vs-popup title="Scope and Expectations" :active.sync="scopePopup" class="offer-modal">
          <div class="mb-5">
            <div class="flex justify-between items-end mb-1">
              <label class="font-semibold text-sm text-dark block">What The Client Should Provide</label>
              <span class="text-xs text-grey">{{ (localData.clientProvides || '').length }} / 500</span>
            </div>
            <vs-textarea class="w-full modern-textarea mb-2" rows="4" maxlength="500" v-model="localData.clientProvides" @blur="saveField('saveOfferClientProvides')"
              placeholder="e.g. Access to relevant information, an internal sponsor and decisions at agreed review points." />
          </div>
          <div class="mb-2">
            <div class="flex justify-between items-end mb-1">
              <label class="font-semibold text-sm text-dark block">Not Included</label>
              <span class="text-xs text-grey">{{ (localData.notIncluded || '').length }} / 500</span>
            </div>
            <vs-textarea class="w-full modern-textarea mb-2" rows="4" maxlength="500" v-model="localData.notIncluded" @blur="saveField('saveOfferNotIncluded')"
              placeholder="Clarify important exclusions or work that would require a separate agreement." />
          </div>
          <div class="flex justify-end mt-6 pt-4 border-t border-grey-light">
            <vs-button color="primary" @click="scopePopup = false">Done</vs-button>
          </div>
        </vs-popup>

      </div>
    </div>
  </vx-card>
</template>

<script>
import FeatherIcon from "../../components/FeatherIcon.vue";
import Options from "./components/Options.vue";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import _ from "lodash";
import draggable from "vuedraggable";
import axios from "axios";

import {
  saveOfferFieldService,
  deleteOfferService,
} from "@/services/editor/offers.service.js";

export default {
  components: {
    FeatherIcon,
    Options,
    VuePerfectScrollbar,
    draggable,
  },
  name: "EditorOffers",
  data() {
    return {
      localData: {},
      addForm: false,
      deletionOfferId: null,
      myConsultantOrgs: [],
      selectedOrgObject: [],
      isLoadingOrgs: true,
      draggableDisabled: false,
      
      // Accordion Toggles
      buyerConfidencePopup: false,
      scopePopup: false,
      isBannerExpanded: false,

      // Swatch Palette
      profileColors: [
        { name: 'Orange', value: '#f29200' },
        { name: 'Green', value: '#10b981' },
        { name: 'Blue', value: '#3b82f6' },
        { name: 'Purple', value: '#8b5cf6' },
        { name: 'Yellow', value: '#F2C244' }
      ],

      // Dropdown Options
      bestFitOptions: [
        'Enterprise Clients', 'Startups & Scaleups', 'SME / Local Business', 'Individual Professionals', 'Agencies & Studios'
      ],
      deliveryWindowOptions: [
        'Within 24 hours', '2-3 Days', '1 Week', '2 Weeks', '1 Month', 'Custom Schedule'
      ],
      paymentBasisOptions: [
        'Pay Upfront', '50% Deposit', 'Upon Completion', 'Milestone Based', 'Monthly Retainer'
      ],
      priceFlexibilityOptions: [
        'Fixed price', 'Negotiable within 10%', 'Value-based pricing'
      ],
      responseTimeOptions: [
        'Within 24 hours', 'Within 48 hours', 'Same day'
      ],
      evidenceFormats: [
        { text: '1-on-1 Video Call', value: '1-on-1 Video Call' },
        { text: 'Document Audit / Review', value: 'Document Audit' },
        { text: 'Live Workshop', value: 'Live Workshop' },
        { text: 'Strategic Action Plan', value: 'Action Plan' },
        { text: 'Ongoing Advisory', value: 'Ongoing Advisory' },
        { text: 'Other / Custom', value: 'Other' }
      ],
      firstStepOptions: [
        { value: 'probono', title: 'Pro bono slot', desc: 'A limited number of free engagements per quarter.' },
        { value: 'call', title: 'Free 15-minute fit call', desc: 'A short call to check scope fit — not a sales call.' },
        { value: 'diagnostic', title: 'Free written mini-diagnostic', desc: 'One page the buyer keeps, before any payment.' },
        { value: 'pilot', title: 'Paid pilot that credits against full fee', desc: 'A small first slice, deducted if they continue.' },
      ],

      settings: {
        suppressScrollX: true,
        maxScrollbarLength: 40,
        wheelSpeed: 0.6,
      },
    };
  },
  computed: {
    saveInProgress() {
      return this.$store.state.editor.saveInProgress;
    },
    isUserLoggedIn() {
      return this.$store.state.auth.isUserLoggedIn;
    },
    isConsultant() {
      const userType = this.$store.state.auth.user.userType;
      return userType === 'coach' || userType === 'consultant'; 
    },
    hasConsultantAccess() {
      return this.myConsultantOrgs.length > 0 || this.isConsultant;
    },
    userId() {
      return this.$store.state.auth.user._id;
    },
    internetConnectionStatus() {
      return this.$store.state.users.internetConnectionStatus;
    },
    disableInputField() {
      return !this.isUserLoggedIn || !this.internetConnectionStatus;
    },
    hasBuyerConfidence() {
      return !!(this.localData.firstStepType || this.localData.firstStepTakeaway || this.localData.responseTime || this.localData.slotsOpen);
    },
    hasScope() {
      return !!(this.localData.clientProvides || this.localData.notIncluded);
    },
    offers: {
      get: function () {
        return this.$store.state.editor.offers || [];
      },
      set: function (newVal) {
        this.$store.commit("editor/SET_OFFERS", newVal);
      },
    },
  },
  mounted() {
    if (this.isUserLoggedIn && this.userId) {
      this.fetchOffers();
      this.fetchOrgs();
    } else {
      this.isLoadingOrgs = false;
    }
  },
  methods: {
    fetchOffers() {
      axios.get(`https://parser.ai.getboarded.com/api/offers/${this.userId}`)
        .then(res => {
          if (res.data.status === 'success') {
            this.$store.commit("editor/SET_OFFERS", res.data.data);
          }
        });
    },
    fetchOrgs() {
      axios.get(`https://parser.ai.getboarded.com/api/v2/consultants/${this.userId}/organizations`)
        .then(res => {
          if (res.data.status === 'success') {
            this.myConsultantOrgs = res.data.data;
          }
        })
        .finally(() => {
          this.isLoadingOrgs = false;
        });
    },
    updateOrder(e) {
      _.debounce(
        function (currentThis) {
          const newOffers = currentThis.offers.map(function (ch, index) {
            return { _id: ch._id, oldSortId: ch.sortId, newSortId: index };
          });

          newOffers.forEach((ch) => {
            currentThis.draggableDisabled = true;
            currentThis.$store
              .dispatch("editor/updateOffersOrder", {
                _id: ch._id,
                sortId: ch.newSortId,
              })
              .then(() => {
                currentThis.draggableDisabled = false;
              });
          });
        },
        800,
        { leading: false, trailing: true }
      )(this);
    },
    checkMove: function (e) {},

    onOrgChange(selectedOrgsArray) {
      if (!selectedOrgsArray) return;
      
      const orgNames = selectedOrgsArray.map(org => org.name);
      const orgIds = selectedOrgsArray.map(org => org.org_id);

      this.localData.organization = orgNames;
      this.localData.organization_id = orgIds;
      
      this.saveField('saveOfferOrganization');
      
      const payload = {
        isUserLoggedIn: this.isUserLoggedIn,
        action: 'saveOfferOrganizationId',
        localData: { _id: this.localData._id, organization_id: orgIds },
        offerId: this.localData._id,
        currentThis: this,
      };
      saveOfferFieldService(payload);
    },

    // Form specific methods
    initializeLocalDataFields() {
       if (!this.localData.deliverables) this.$set(this.localData, 'deliverables', ['']);
       if (!this.localData.priceFlexibility) this.$set(this.localData, 'priceFlexibility', 'Fixed price');
       if (this.localData.valueExchangeAccepted === undefined) this.$set(this.localData, 'valueExchangeAccepted', false);
       if (!this.localData.profileColor) this.$set(this.localData, 'profileColor', '#f29200');
    },
    adjustTextareaHeight(e) {
      if (!e || !e.target) return;
      const el = e.target;
      el.style.height = 'auto';
      el.style.height = el.scrollHeight + 'px';
    },
    getDeliverablePlaceholder(index) {
        const placeholders = ['e.g. Baseline assessment', 'e.g. Prioritized opportunity matrix', 'e.g. 90-day action plan with owners'];
        return placeholders[index % placeholders.length];
    },
    addDeliverable() {
        this.localData.deliverables.push('');
    },
    // ADD THIS NEW METHOD RIGHT HERE:
    updateDeliverable(index, val) {
        this.$set(this.localData.deliverables, index, val);
    },
    removeDeliverable(index) {
        this.localData.deliverables.splice(index, 1);
        this.saveField('saveOfferDeliverables');
        // Removed the line that forces a new item to appear when empty
    },
    toggleFirstStep(value) {
        if (this.localData.firstStepType === value) {
          this.$set(this.localData, 'firstStepType', null);
        } else {
          this.$set(this.localData, 'firstStepType', value);
        }
        this.saveField('saveOfferFirstStep');
    },
    setProfileColor(colorHex) {
        this.$set(this.localData, 'profileColor', colorHex);
        this.saveField('saveOfferProfileColor');
    },

    addOffer() {
      const sortId = this.offers.length + 1;
      this.$store
        .dispatch("editor/createOffer", { userId: this.userId, sortId })
        .then((res) => {
          this.localData = res.data; 
          this.initializeLocalDataFields();
          
          this.addForm = true;
          this.selectedOrgObject = [];
          this.buyerConfidencePopup = false;
          this.scopePopup = false;
          this.isBannerExpanded = false;
          
          this.$forceUpdate();
        })
        .catch((err) => {
          console.error(err);
          this.$vs.notify({ title: "Error creating offer", text: "Check console for details", color: "danger" });
        });
    },
    editOffer(data) {
      this.localData = JSON.parse(JSON.stringify(data));
      this.initializeLocalDataFields();
      
      // Expand sections if data exists in them
      this.buyerConfidencePopup = false;
      this.scopePopup = false;
      this.isBannerExpanded = !!this.localData.profileColor;

      // Populate multi-select
      this.selectedOrgObject = [];
      if (this.localData.organization_id && this.myConsultantOrgs.length > 0) {
        const idsArray = Array.isArray(this.localData.organization_id) 
          ? this.localData.organization_id 
          : [this.localData.organization_id];
        
        this.selectedOrgObject = this.myConsultantOrgs.filter(org => idsArray.includes(org.org_id));
      }

      this.addForm = true;
    },
    deleteOffer(id) {
      this.deletionOfferId = id;
      this.$vs.dialog({
        type: "confirm",
        color: "danger",
        title: `Confirm Deletion`,
        text: "Are you sure you want to remove this Offer from your public profile?",
        acceptText: "Delete",
        accept: this.confirmDeleteOffer,
      });
    },
    confirmDeleteOffer() {
      const payload = {
        isUserLoggedIn: this.isUserLoggedIn,
        offerId: this.deletionOfferId,
        currentThis: this,
      };
      deleteOfferService(payload);
      if (this.localData._id === this.deletionOfferId) {
         this.addForm = false;
         this.localData = {};
      }
    },
    async saveField(action) {
      const payload = {
        isUserLoggedIn: this.isUserLoggedIn,
        action,
        localData: this.localData,
        offerId: this.localData._id,
        currentThis: this,
      };
      saveOfferFieldService(payload);
    },
    
  },
  created() {
    this.$store.dispatch("editor/toggleRightBar", true);
    this.$store.dispatch("editor/togglePreviewMode", false);
  }
};
</script>

<style scoped>
/* Scroll Area */
.scroll-area--list { 
  position: relative; 
  margin: auto; 
  width: 100%; 
  height: auto;
  max-height: 60vh;
  min-height: 100px;
}

/* Modern Input Overrides matching standard UI */
.modern-input >>> .vs-input--input,
.modern-input >>> .vs-select--input {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 14px;
  color: #1a202c;
  box-shadow: 0 1px 2px rgba(0,0,0,0.02);
}
.modern-input >>> .vs-input--input:focus,
.modern-input >>> .vs-select--input:focus {
  border-color: #f6ad55 !important;
  box-shadow: 0 0 0 3px rgba(237, 137, 54, 0.15) !important;
}

.modern-textarea >>> .vs-textarea {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 14px;
  font-family: inherit;
  color: #1a202c;
  box-shadow: 0 1px 2px rgba(0,0,0,0.02);
}
.modern-textarea >>> .vs-textarea:focus {
  border-color: #f6ad55 !important;
  box-shadow: 0 0 0 3px rgba(237, 137, 54, 0.15) !important;
}

.dynamic-textarea >>> .vs-textarea {
  min-height: 42px !important;
  overflow-y: hidden;
  resize: none;
}

/* Toggles & Cards */
.bg-warning-light {
  background-color: #fffaf0 !important;
}
.border-warning {
  border-color: #ed8936 !important;
}

.ghost-card {
  opacity: 0.5;
  background: #f8f9fa;
  border: 1px dashed #cbd5e0;
}

/* Clearfix utility */
.clear-both {
  clear: both;
}

/* Custom Text Colors matching Projects tab */
.text-dark { color: #2d3748; }
.text-grey-dark { color: #718096; }
.text-grey { color: #a0aec0; }
.text-grey-light { color: #e2e8f0; }
.bg-grey-lightest { background-color: #f7fafc; }
.force-left-align >>> .vs-input--input {
  text-align: left !important;
}
.add-text-btn {
  font-size: 16px;
  font-weight: 800;
  color: #F2C244;
  text-decoration: underline;
  text-underline-offset: 6px;
  text-decoration-thickness: 3px;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.2s ease;
  display: inline-block;
  letter-spacing: -0.5px;
}

.add-text-btn:hover {
  opacity: 0.8;
  transform: translateY(-1px);
}
.offer-modal >>> .vs-popup {
  width: 600px;
  max-width: 95vw;
}
.offer-modal >>> .vs-popup--content {
  padding: 1rem;
}
.stretchy-textarea {
  display: flex;
  flex-direction: column;
}
.stretchy-textarea >>> .vs-textarea {
  flex-grow: 1;
  height: 100% !important;
  min-height: 100px;
  resize: none;
}
</style>